import SubstringPopover from '@/components/compliance-assistant/SubstringPopover'
import { Button } from '@/components/ui/button'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { rutsKeys } from './constants'
import Cancel from '@/assets/Cancel'
import Mailbox from '@/assets/Mailbox'
import Warning from '@/assets/Warning'

export default function RutsTable({
  processData,
  setProcessData,
  rutKey,
  isViewMode,
}: {
  processData: FormProcessData
  setProcessData: Dispatch<SetStateAction<FormProcessData>>
  rutKey: rutsKeys
  isViewMode: boolean
}) {
  const parentRef = useRef<HTMLDivElement>(null)
  const [hasData, setHasData] = useState(false)
  const [isDirty, setIsDirty] = useState(false)

  const rowVirtualizer = useVirtualizer({
    getScrollElement: () => parentRef.current,
    estimateSize: () => 35,
    count: processData?.[rutKey]?.length ?? 0,
  })

  const handleDelete = (index: number) => {
    const newRuts = [...(processData[rutKey] || [])]
    newRuts.splice(index, 1)
    setProcessData((prev) => ({
      ...prev,
      [rutKey]: newRuts,
    }))
  }

  useEffect(() => {
    // Marcar isDirty como verdadero solamente si antes había información y ahora no.
    if (hasData && !processData?.[rutKey]?.length) {
      setHasData(false)
      setIsDirty(true)
    } else if (processData?.[rutKey]?.length) {
      setHasData(true)
      setIsDirty(false)
    }
  }, [processData])

  return (
    <div className="flex flex-col w-full">
      <div className="flex w-full border border-gray-200 rounded-lg">
        {hasData ? (
          <div
            ref={parentRef}
            style={{
              height: '288px',
              overflowY: 'auto',
            }}
            className="w-full"
          >
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualItem) => (
                <div
                  key={virtualItem.key}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: `${virtualItem.size}px`,
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                  className="border-b px-4 border-gray-200 flex items-center"
                >
                  <div className="grid grid-cols-7 items-center">
                    <div className="col-span-6">
                      <SubstringPopover
                        string={processData?.[rutKey]?.[virtualItem.index] || ''}
                        maxLength={13}
                      />
                    </div>
                    <div className="my-2">
                      {!isViewMode && (
                        <Button
                          aria-label="Eliminar"
                          className="w-4 h-4 ml-1 flex items-center bg-gray-300 rounded-full opacity-70 hover:opacity-100"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(virtualItem.index)}
                        >
                          <Cancel />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div
            style={{
              height: '288px',
            }}
            className="flex flex-col items-center justify-center w-full text-base text-muted-foreground opacity-50"
          >
            <Mailbox />
            <div className="text-sm">Sin datos cargados</div>
          </div>
        )}
      </div>
      {isDirty && (
        <div className="flex items-center mt-2 gap-1 text-red-600">
          <Warning className="w-3 h-3" />
          <span className="text-[0.8rem]">Carga de archivo requerida</span>
        </div>
      )}
    </div>
  )
}
